/**
 * What the builder offers when there is not yet a flow to draw (ACL-478).
 *
 * Three states the page used to collapse into one. A tenant with no tree, a tenant whose tree has
 * no version yet, and a tenant looking at a published version all reached the same screen, whose
 * primary action was "Make a draft" — and with no tree that action can only ever fail, because
 * `POST /intake-flows/draft` refuses with "this tenant has no tree yet: make one before drafting
 * a flow". The mutation had no error handler either, so the click did nothing and said nothing.
 *
 * Which of the three a reader is in is a rule rather than a chain of `&&` in the markup, so it
 * has assertions (`test/intake-builder-state.test.ts`) instead of needing a tenant in each state
 * to click through.
 */

export type BuilderState =
  /** No tree at all: the thing to make first is the tree, not a draft. */
  | "no-tree"
  /** A tree, but no version in it yet. Drafting is the right offer. */
  | "no-flow"
  /** A published or retired version is on screen; editing means taking a draft of it. */
  | "read-only"
  /** A draft is open and editable. */
  | "editing";

export function builderState(treeCount: number, hasFlow: boolean, isDraft: boolean): BuilderState {
  // The tree is checked first and on its own: with none, neither "Make a draft" nor the draft
  // toolbar can do anything, whatever the flow looks like.
  if (treeCount === 0) return "no-tree";
  if (!hasFlow) return "no-flow";
  return isDraft ? "editing" : "read-only";
}

/** Whether the page should offer to make a draft. False with no tree, where it would 409. */
export function mayDraft(state: BuilderState): boolean {
  return state === "no-flow" || state === "read-only";
}
